<template>
  <Transition name="mobile-modal" appear>
    <div
      v-if="isMobile && isOpen"
      class="tw:fixed tw:h-screen tw:w-full tw:bg-white tw:left-0 tw:top-0 tw:z-30 tw:flex tw:flex-col"
    >
      <div class="tw:flex tw:flex-col tw:items-center tw:p-6 tw:pb-4">
        <h2 class="tw:heading-3 tw:lg:heading-1 tw:text-black tw:mb-4">{{ trans._register.platforms.modal.title }}</h2>

        <!-- Search input for mobile -->
        <div class="tw:w-full tw:max-w-sm">
          <EdroneInput
            v-model="searchQuery"
            type="default"
            :placeholder="trans._register.platforms.modal.search.placeholder"
            class="tw:w-full"
          >
            <template #icon>
              <font-awesome-icon icon="fa-light fa-magnifying-glass" />
            </template>
          </EdroneInput>
        </div>
      </div>

      <div class="tw:flex-1 tw:overflow-y-auto tw:px-6">
        <div v-if="isLoading" class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-3 tw:pb-4">
          <EdroneSkeletonLoader v-for="n in 6" :key="n" class="tw:h-20 tw:sm:h-24 tw:rounded-sm" />
        </div>
        <div v-else-if="otherPlatforms.length === 0" class="tw:text-center tw:py-12">
          <div v-if="searchQuery.trim()">
            <font-awesome-icon icon="fa-light fa-magnifying-glass" class="tw:text-4xl tw:text-default-400 tw:mb-4" />
            <p class="tw:body-1 tw:text-default-600 tw:mb-2">
              {{ trans._register.platforms.modal.search.noResults }}
            </p>
            <p class="tw:body-2 tw:text-default-500">
              {{ trans._register.platforms.modal.search.tryDifferentKeywords }}
            </p>
          </div>
          <div v-else>
            <p class="tw:body-1 tw:text-default-600 tw:mb-2">{{ trans._register.platforms.modal.noAvailable }}</p>
            <p class="tw:body-2 tw:text-default-500">{{ trans._register.platforms.modal.tryAgainLater }}</p>
          </div>
        </div>
        <div v-else class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-3 tw:pb-4">
          <button
            v-for="platform in otherPlatforms"
            :key="platform.platform"
            class="tw:flex tw:items-center tw:justify-center tw:border tw:rounded-sm tw:cursor-pointer tw:p-4 tw:h-20 tw:sm:h-24 tw:transition-all tw:duration-200"
            :class="{
              'tw:bg-blue-000 tw:border-blue-500 tw:hover:border-blue-600 tw:shadow-md':
                selectedPlatform === platform.platform,
              'tw:bg-white tw:border-default-200 tw:hover:border-blue-500 tw:hover:shadow-xs tw:hover:scale-[1.02]':
                selectedPlatform !== platform.platform,
            }"
            :aria-label="`${trans._register.platforms.select} ${platform.name}`"
            @click="emit('select', platform.platform)"
            @keydown.enter="emit('select', platform.platform)"
            @keydown.space.prevent="emit('select', platform.platform)"
          >
            <img
              v-if="platform.platform !== Platforms.Custom"
              :src="platform.logo"
              :alt="platform.name"
              class="tw:h-16 tw:sm:h-20 tw:w-auto tw:max-w-24 tw:sm:max-w-32 tw:object-contain"
              loading="lazy"
              :onerror="`this.style.display='none'`"
            />
            <div v-else class="tw:p-3 tw:sm:p-4 tw:text-center">
              <p class="tw:caption tw:sm:body-2 tw:text-default-700">
                {{ trans._register.platforms.modal.other.title }}
              </p>
              <p class="tw:body-2 tw:sm:body-1 tw:text-black">{{ trans._register.platforms.modal.other.subtitle }}</p>
            </div>
          </button>
        </div>
      </div>

      <div class="tw:p-6 tw:pt-4 tw:border-t tw:border-default-200 tw:bg-default-000">
        <EdroneButton class="tw:w-full" type="outline" @click="emit('close')">
          {{ trans._register.platforms.modal.close }}
        </EdroneButton>
      </div>
    </div>
  </Transition>
  <EdroneModal v-if="!isMobile" :open="isOpen" :width="1080" @close="emit('close')">
    <div class="tw:flex tw:flex-col tw:gap-6 tw:lg:gap-8 tw:p-6">
      <div class="tw:flex tw:flex-col tw:lg:items-center">
        <h2 class="tw:heading-1 tw:text-black">{{ trans._register.platforms.modal.title }}</h2>
      </div>

      <!-- Search input -->
      <div class="tw:max-w-md tw:mx-auto tw:w-full">
        <EdroneInput
          v-model="searchQuery"
          type="default"
          :placeholder="trans._register.platforms.modal.search.placeholder"
          class="tw:w-full"
        >
          <template #icon>
            <font-awesome-icon icon="fa-light fa-magnifying-glass" />
          </template>
        </EdroneInput>
      </div>

      <div class="tw:overflow-y-auto tw:max-h-96 tw:min-h-[12rem] tw:p-1">
        <div v-if="isLoading" class="tw:grid tw:grid-cols-2 tw:lg:grid-cols-3 tw:xl:grid-cols-4 tw:gap-3 tw:lg:gap-4">
          <EdroneSkeletonLoader v-for="n in 12" :key="n" class="tw:h-20 tw:lg:h-24 tw:rounded-sm" />
        </div>
        <div v-else-if="otherPlatforms.length === 0" class="tw:text-center tw:py-12">
          <div v-if="searchQuery.trim()">
            <font-awesome-icon icon="fa-light fa-magnifying-glass" class="tw:text-4xl tw:text-default-400 tw:mb-4" />
            <p class="tw:body-1 tw:text-default-600 tw:mb-2">
              {{ trans._register.platforms.modal.search.noResults }}
            </p>
            <p class="tw:body-2 tw:text-default-500">
              {{ trans._register.platforms.modal.search.tryDifferentKeywords }}
            </p>
          </div>
          <div v-else>
            <p class="tw:body-1 tw:text-default-600 tw:mb-2">{{ trans._register.platforms.modal.noAvailable }}</p>
            <p class="tw:body-2 tw:text-default-500">{{ trans._register.platforms.modal.tryAgainLater }}</p>
          </div>
        </div>
        <div v-else class="tw:grid tw:grid-cols-2 tw:lg:grid-cols-3 tw:xl:grid-cols-4 tw:gap-2 tw:lg:gap-2">
          <button
            v-for="platform in otherPlatforms"
            :key="platform.platform"
            class="tw:flex tw:items-center tw:justify-center tw:border tw:rounded-lg tw:cursor-pointer tw:p-3 tw:lg:p-4 tw:h-20 tw:lg:h-24 tw:duration-200 tw:transition-all"
            :class="{
              'tw:bg-blue-000 tw:border-blue-500 tw:hover:border-blue-600 tw:shadow-md':
                selectedPlatform === platform.platform,
              'tw:bg-white tw:border-default-200 tw:hover:border-blue-500 tw:hover:shadow-xs tw:hover:scale-[1.02] tw:focus:ring-2 tw:focus:ring-blue-500 tw:focus:ring-offset-2':
                selectedPlatform !== platform.platform,
            }"
            :aria-label="`${trans._register.platforms.select} ${platform.name}`"
            :aria-pressed="selectedPlatform === platform.platform"
            @click="emit('select', platform.platform)"
            @keydown.enter="emit('select', platform.platform)"
            @keydown.space.prevent="emit('select', platform.platform)"
          >
            <img
              v-if="platform.platform !== Platforms.Custom"
              :src="platform.logo"
              :alt="platform.name"
              class="tw:h-12 tw:sm:h-16 tw:lg:h-20 tw:w-auto tw:max-w-16 tw:sm:max-w-20 tw:lg:max-w-24 tw:object-contain tw:transition-transform tw:duration-200"
              loading="lazy"
              :onerror="`this.style.display='none'`"
            />
            <div v-else class="tw:p-2 tw:lg:p-4 tw:text-center">
              <p class="tw:caption tw:lg:body-2 tw:text-default-700">
                {{ trans._register.platforms.modal.other.title }}
              </p>
              <p class="tw:body-2 tw:lg:body-1 tw:text-black">{{ trans._register.platforms.modal.other.subtitle }}</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  </EdroneModal>
</template>

<script setup lang="ts">
import EdroneModal from "edrone-components/EdroneModal.vue";
import { computed, ref } from "vue";
import { Platforms } from "vue-app/types/enums";
import { platformData, languagePlatforms } from "./RegisterPlatforms.constants";
import useDeviceResolutionDetector from "vue-app/composables/useDeviceResolutionDetector";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneSkeletonLoader from "edrone-components/EdroneSkeletonLoader.vue";
import EdroneInput from "edrone-components/EdroneInput.vue";

interface Props {
  isOpen: boolean;
  language?: string;
  selectedPlatform?: Platforms;
  isLoading?: boolean;
}

const { isOpen, language = "pl", isLoading = false } = defineProps<Props>();

interface Emits {
  (e: "close"): void;
  (e: "select", platform: Platforms): void;
  (e: "search", query: string): void;
}

const emit = defineEmits<Emits>();

const { isMobile } = useDeviceResolutionDetector(true);

// Search functionality
const searchQuery = ref("");

const platformsForLanguage = computed(() => {
  return new Set(languagePlatforms[language] || []);
});

const filteredOtherPlatforms = computed(() => {
  const allOtherPlatforms = Object.entries(platformData)
    .filter(([platform]) => !platformsForLanguage.value.has(platform as Platforms))
    .map(([platform, data]) => ({
      platform: platform as Platforms,
      ...data,
    }));

  if (!searchQuery.value.trim()) {
    return allOtherPlatforms.sort((a, b) => {
      if (a.platform === Platforms.Custom) return 1;
      if (b.platform === Platforms.Custom) return -1;
      return a.name.localeCompare(b.name);
    });
  }

  return allOtherPlatforms
    .filter((platform) => platform.name.toLowerCase().includes(searchQuery.value.toLowerCase().trim()))
    .sort((a, b) => {
      if (a.platform === Platforms.Custom) return 1;
      if (b.platform === Platforms.Custom) return -1;
      return a.name.localeCompare(b.name);
    });
});

// Legacy computed for backwards compatibility
const otherPlatforms = computed(() => filteredOtherPlatforms.value);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const trans = (window as any)._trans;
</script>

<style scoped>
.mobile-modal-enter-active,
.mobile-modal-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.mobile-modal-enter-from {
  opacity: 0;
  transform: translateY(100%);
}

.mobile-modal-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.mobile-modal-enter-to,
.mobile-modal-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
